/**
 * Document Intelligence — shared types.
 *
 * A document category (Documents Master) is the extraction schema for one kind
 * of document: when an upload is processed, the LLM first picks its category
 * from these, then extracts exactly that category's fields, reading each
 * field's description, format and validation criteria as its instructions.
 */

/** The type an extracted value is expected to have. */
export type DocumentFieldDataType = 'String' | 'Integer' | 'Decimal' | 'Date' | 'Boolean';

export const DOCUMENT_FIELD_DATA_TYPES: DocumentFieldDataType[] = [
  'String', 'Integer', 'Decimal', 'Date', 'Boolean',
];

/** The languages Document Intelligence handles. Mirrors the backend's
 *  app/constants/document_languages.py — codes are ISO 639-1, the same ones
 *  Azure Document Intelligence takes as its language hint. */
export type DocumentLanguage = 'en' | 'ar';

export const DOCUMENT_LANGUAGES: { value: DocumentLanguage; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'Arabic' },
];

export const DEFAULT_DOCUMENT_LANGUAGE: DocumentLanguage = 'en';

/** For list filters: "All languages" first, so a list shows everything until narrowed. */
export const DOCUMENT_LANGUAGE_FILTER_OPTIONS: { value: DocumentLanguage | ''; label: string }[] = [
  { value: '', label: 'All languages' },
  ...DOCUMENT_LANGUAGES,
];

export const languageLabel = (code: string | null | undefined): string =>
  DOCUMENT_LANGUAGES.find((l) => l.value === code)?.label || code || 'English';

/** One field worth extracting from documents of a category. */
export interface DocumentCategoryField {
  fieldName: string;
  dataType: DocumentFieldDataType;
  /** What the field is — the LLM reads this to find it. */
  description: string;
  /** How the value usually looks, ideally with an example. */
  format: string;
  /** What counts as a valid value. */
  validationCriteria: string;
  /** Whether a document of this category must contain it. Nuance ("optional
   *  when absent from the document") belongs in the description. */
  required: boolean;
  /** Whether the field holds personal or confidential data (a name, an ID
   *  number, a medical detail). */
  isSensitive: boolean;
}

/** One document category — a document_schema_master row. */
export interface DocumentCategory {
  _id: string;
  category: string;
  /** What belongs in this category — read by the classifier to tell them apart. */
  categoryDescription?: string;
  /** The language the category is written for. */
  language: DocumentLanguage;
  fields: DocumentCategoryField[];
  isActive: boolean;
  isDelete: boolean;
  /** Derived by the backend from `fields`; the UI can also count them itself. */
  fieldCount?: number;
  requiredCount?: number;
  createdBy?: string | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

// ---------------------------------------------------------------------------
// Processing a document
// ---------------------------------------------------------------------------

/** The pipeline's stages, in the order they run. */
/** `translate` runs only for documents not in English. */
export type ProcessingStep = 'read' | 'classify' | 'extract' | 'translate';

/** One progress event from the processing stream. */
export interface DocumentProcessingEvent {
  step: ProcessingStep | 'pipeline' | 'done';
  status: 'started' | 'done' | 'failed';
  message?: string;
  error?: string;
  code?: number;
  steps?: ProcessingStep[];
  fileName?: string;
  /** read */
  pageCount?: number;
  characters?: number;
  model?: string;
  /** classify */
  category?: string;
  confidence?: number;
  reason?: string;
  /** extract */
  fieldCount?: number;
  missingRequired?: number;
  /** translate */
  translated?: number;
  /** Set on a step that finished but not fully — a translation that was
   *  unavailable reports done with a warning rather than failing the run. */
  warning?: string;
  /** done */
  result?: DocumentProcessingResult;
}

/** One field the LLM was asked to extract. */
export interface ExtractedField {
  fieldName: string;
  dataType: string;
  required: boolean;
  value: string | number | null;
  confidence: number;
  /** True when the document did not state it. */
  missing: boolean;
  /** What the model read. Sent on save beside `value`: equal when nobody
   *  corrected the field, different when someone did. */
  aiValue?: string | number | null;
  /** English translation of `value`, for a document not in English. */
  translatedValue?: string | number | null;
}

/** What one processed document produced.
 *
 *  Nothing is stored when this comes back: the operator looks at it on the
 *  result screen and saves it there if it is worth keeping, which is what turns
 *  it into a `SavedDocument`.
 */
export interface DocumentProcessingResult {
  fileName: string;
  fileType: string | null;
  fileSize: number;
  /** The language chosen on the upload page. */
  language: DocumentLanguage;
  ocr: {
    model: string | null;
    pageCount: number;
    characters: number;
    contentFormat: string;
    latencyMs: number;
    /** The whole document as text — what the LLM was given. */
    text: string;
  } | null;
  classification: {
    category: string;
    categoryId: string | null;
    confidence: number;
    reason: string;
    /** What the model answered when it named no configured category. */
    answered: string | null;
    model: string | null;
    usage: Record<string, number>;
  } | null;
  /** Null when the category could not be identified — nothing to extract against. */
  extraction: {
    fields: ExtractedField[];
    summary: string;
    missingRequired: string[];
    extraKeysIgnored: string[];
    model: string | null;
    usage: Record<string, number>;
    /** Documents not in English: the summary in English, and why the
     *  translation was unavailable when it was. */
    translatedSummary?: string | null;
    translationError?: string | null;
    translationUsage?: Record<string, number>;
  } | null;
  cost: {
    ocr: number | null;
    classify: number | null;
    extract: number | null;
    translate?: number | null;
    currency: string | null;
  };
  totalMs?: number;
}

// ---------------------------------------------------------------------------
// A saved document (document_extraction)
// ---------------------------------------------------------------------------

export type SavedDocumentStatus = 'COMPLETED' | 'FAILED' | 'PROCESSING';

/** A field on a saved document; carries the AI's own answer once corrected. */
export interface SavedField extends ExtractedField {
  /** What the model read, kept from the first correction onwards. */
  aiValue?: string | number | null;
  editedBy?: string | null;
  editedAt?: string | null;
}

export interface SavedDocument {
  _id: string;
  appId?: string | null;
  fileName: string;
  fileType: string | null;
  fileSize: number;
  /** The language it was processed as. */
  language: DocumentLanguage;
  fileSha256: string;
  storage: { provider: string; container: string; blobName: string } | null;
  status: SavedDocumentStatus;
  error: string | null;
  category: string;
  classification: {
    schemaId: string | null;
    schemaUpdatedAt: string | null;
    category: string | null;
    confidence: number | null;
    reason: string | null;
    answered: string | null;
    model: string | null;
  };
  summary: string;
  /** Documents not in English: the summary in English. */
  translatedSummary?: string | null;
  translationError?: string | null;
  fields: SavedField[];
  missingRequired: string[];
  editedCount: number;
  reviewedBy: string | null;
  reviewedAt: string | null;
  ocr: {
    model: string | null;
    pageCount: number | null;
    characters: number | null;
    contentFormat: string | null;
    latencyMs: number | null;
  } | null;
  /** Only when asked for with include_text. */
  text?: string;
  usage?: Record<string, Record<string, number>>;
  cost: {
    ocr: number | null; classify: number | null; extract: number | null;
    translate?: number | null; currency: string | null;
  };
  totalMs: number | null;
  /** Minted per read, short-lived. Null when the file was never stored. */
  fileUrl?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

/** Create/update payload — the editable part of a category. */
export interface DocumentCategoryInput {
  category: string;
  categoryDescription: string;
  language: DocumentLanguage;
  fields: DocumentCategoryField[];
  isActive: boolean;
}
